/**
 * VAIR 8-Player Algorithm
 *
 * Based on official VAIR 8-Player Score Card v4.15.25
 *
 * Structure:
 * - 7 games total
 * - 2 courts (2 simultaneous matches per game)
 * - Each player plays 7 games
 * - Each player partners with every other player exactly once (perfect rotation)
 * - Each player faces every other player exactly twice as opponent
 */
import { VairRound, VairSchedule } from './types';

/**
 * Generate VAIR schedule for 8 players
 * Based on the standard VAIR score card pattern:
 * - 7 games total
 * - Each game has 2 matches (4 players per match)
 * - Each player plays in exactly 7 games
 */
export function generateVairSchedule8Players(timePerMatch: number = 20): VairSchedule {
  let currentTime = 0;
  const rounds: VairRound[] = [];

  // Game 1: "1-5 vs 7-8" and "2-3 vs 4-6"
  rounds.push({
    matches: [
      {
        team1: { player1: 0, player2: 4 }, // Players 1-5 (0-indexed: 0, 4)
        team2: { player1: 6, player2: 7 }, // Players 7-8 (0-indexed: 6, 7)
        court: 0,
        time: currentTime,
      },
      {
        team1: { player1: 1, player2: 2 }, // Players 2-3 (0-indexed: 1, 2)
        team2: { player1: 3, player2: 5 }, // Players 4-6 (0-indexed: 3, 5)
        court: 1,
        time: currentTime,
      },
    ],
    byes: [],
  });

  currentTime += timePerMatch;

  // Game 2: "4-7 vs 6-8" and "1-2 vs 3-5"
  rounds.push({
    matches: [
      {
        team1: { player1: 3, player2: 6 }, // Players 4-7 (0-indexed: 3, 6)
        team2: { player1: 5, player2: 7 }, // Players 6-8 (0-indexed: 5, 7)
        court: 0,
        time: currentTime,
      },
      {
        team1: { player1: 0, player2: 1 }, // Players 1-2 (0-indexed: 0, 1)
        team2: { player1: 2, player2: 4 }, // Players 3-5 (0-indexed: 2, 4)
        court: 1,
        time: currentTime,
      },
    ],
    byes: [],
  });

  currentTime += timePerMatch;

  // Game 3: "2-6 vs 1-8" and "3-4 vs 5-7"
  rounds.push({
    matches: [
      {
        team1: { player1: 1, player2: 5 }, // Players 2-6 (0-indexed: 1, 5)
        team2: { player1: 0, player2: 7 }, // Players 1-8 (0-indexed: 0, 7)
        court: 0,
        time: currentTime,
      },
      {
        team1: { player1: 2, player2: 3 }, // Players 3-4 (0-indexed: 2, 3)
        team2: { player1: 4, player2: 6 }, // Players 5-7 (0-indexed: 4, 6)
        court: 1,
        time: currentTime,
      },
    ],
    byes: [],
  });

  currentTime += timePerMatch;

  // Game 4: "1-6 vs 4-5" and "3-7 vs 2-8"
  rounds.push({
    matches: [
      {
        team1: { player1: 0, player2: 5 }, // Players 1-6 (0-indexed: 0, 5)
        team2: { player1: 3, player2: 4 }, // Players 4-5 (0-indexed: 3, 4)
        court: 0,
        time: currentTime,
      },
      {
        team1: { player1: 2, player2: 6 }, // Players 3-7 (0-indexed: 2, 6)
        team2: { player1: 1, player2: 7 }, // Players 2-8 (0-indexed: 1, 7)
        court: 1,
        time: currentTime,
      },
    ],
    byes: [],
  });

  currentTime += timePerMatch;

  // Game 5: "5-6 vs 2-7" and "1-4 vs 3-8"
  rounds.push({
    matches: [
      {
        team1: { player1: 4, player2: 5 }, // Players 5-6 (0-indexed: 4, 5)
        team2: { player1: 1, player2: 6 }, // Players 2-7 (0-indexed: 1, 6)
        court: 0,
        time: currentTime,
      },
      {
        team1: { player1: 0, player2: 3 }, // Players 1-4 (0-indexed: 0, 3)
        team2: { player1: 2, player2: 7 }, // Players 3-8 (0-indexed: 2, 7)
        court: 1,
        time: currentTime,
      },
    ],
    byes: [],
  });

  currentTime += timePerMatch;

  // Game 6: "4-8 vs 2-5" and "6-7 vs 1-3"
  rounds.push({
    matches: [
      {
        team1: { player1: 3, player2: 7 }, // Players 4-8 (0-indexed: 3, 7)
        team2: { player1: 1, player2: 4 }, // Players 2-5 (0-indexed: 1, 4)
        court: 0,
        time: currentTime,
      },
      {
        team1: { player1: 5, player2: 6 }, // Players 6-7 (0-indexed: 5, 6)
        team2: { player1: 0, player2: 2 }, // Players 1-3 (0-indexed: 0, 2)
        court: 1,
        time: currentTime,
      },
    ],
    byes: [],
  });

  currentTime += timePerMatch;

  // Game 7: "1-7 vs 2-4" and "3-6 vs 5-8"
  rounds.push({
    matches: [
      {
        team1: { player1: 0, player2: 6 }, // Players 1-7 (0-indexed: 0, 6)
        team2: { player1: 1, player2: 3 }, // Players 2-4 (0-indexed: 1, 3)
        court: 0,
        time: currentTime,
      },
      {
        team1: { player1: 2, player2: 5 }, // Players 3-6 (0-indexed: 2, 5)
        team2: { player1: 4, player2: 7 }, // Players 5-8 (0-indexed: 4, 7)
        court: 1,
        time: currentTime,
      },
    ],
    byes: [],
  });

  return { rounds };
}

/**
 * Validate that the VAIR schedule is correct
 * - Each player should play in exactly (numPlayers - 1) games (for 8 players = 7 games)
 * - No player should play with the same partner twice
 * - All players should be paired in each game (unless odd number)
 */
export function validateVairSchedule8Players(
  schedule: VairSchedule,
  numPlayers: number = 8,
): boolean {
  const playerGameCount: Map<number, number> = new Map();
  const playerPartners: Map<number, Set<number>> = new Map();

  // Initialize maps
  for (let i = 0; i < numPlayers; i++) {
    playerGameCount.set(i, 0);
    playerPartners.set(i, new Set());
  }

  // Track player appearances and partners
  schedule.rounds.forEach((round) => {
    round.matches.forEach((match) => {
      // Team 1
      const team1Players = [match.team1.player1, match.team1.player2];
      team1Players.forEach((player) => {
        playerGameCount.set(player, (playerGameCount.get(player) || 0) + 1);
        // Track partner
        const partner = team1Players.find((p) => p !== player);
        if (partner !== undefined) {
          playerPartners.get(player)?.add(partner);
        }
      });

      // Team 2
      const team2Players = [match.team2.player1, match.team2.player2];
      team2Players.forEach((player) => {
        playerGameCount.set(player, (playerGameCount.get(player) || 0) + 1);
        // Track partner
        const partner = team2Players.find((p) => p !== player);
        if (partner !== undefined) {
          playerPartners.get(player)?.add(partner);
        }
      });
    });
  });

  // Validate: Each player should play in (numPlayers - 1) games
  const expectedGames = numPlayers - 1;
  for (let i = 0; i < numPlayers; i++) {
    const gamesPlayed = playerGameCount.get(i) || 0;
    if (gamesPlayed !== expectedGames) {
      console.error(`Player ${i + 1} played ${gamesPlayed} games, expected ${expectedGames}`);
      return false;
    }
  }

  return true;
}
